import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getClientId, getCriticalityLabels } from 'src/module/authentication/selector';
import { getDevicesSelectedFilters } from 'src/module/filter/selector';
import { fetchDeviceList, fetchDeviceListNextPage } from 'src/module/deviceList/action';
import {
  getDeviceListError,
  getDeviceListHasMore,
  getDeviceListItems,
  getDeviceListLoading
} from 'src/module/deviceList/selector';
import { deviceListScopeKey, type DeviceListScope } from 'src/service/api/deviceList';
import { toDeviceListRows } from './deviceListRows';
import { deviceFilterChipKeys } from './DeviceFilterChips';

export default function useDeviceList (sort: string, scope?: DeviceListScope, search = '') {
  const dispatch = useDispatch();

  const clientId = useSelector(getClientId);
  const selectedFilters = useSelector(getDevicesSelectedFilters);
  const labels = useSelector(getCriticalityLabels);
  const items = useSelector(getDeviceListItems);
  const loading = useSelector(getDeviceListLoading);
  const hasMore = useSelector(getDeviceListHasMore);
  const error = useSelector(getDeviceListError);

  const filtersHash = selectedFilters.hashCode();
  const scopeKey = deviceListScopeKey(scope);
  const countKeys = useMemo(() => deviceFilterChipKeys(labels), [labels]);
  const countKeysKey = countKeys.join(',');

  useEffect(() => {
    dispatch(fetchDeviceList(selectedFilters, sort, scope, countKeys, search));
  }, [dispatch, clientId, filtersHash, sort, scopeKey, countKeysKey, search]);

  const filtered = selectedFilters.some(Boolean) || Boolean(search);

  const rows = useMemo(() => toDeviceListRows(items, labels, filtered), [items, labels, filtered]);
  const onLoadMore = useCallback(() => dispatch(fetchDeviceListNextPage()), [dispatch]);

  return { rows, loading, hasMore: hasMore && !error, onLoadMore, error, filtered };
}
